import { usePortalProfileQuery } from "@dashboard/graphql";
import { Box, Button, Text } from "@saleor/macaw-ui-next";
import { Fragment, useState } from "react";
import { useIntl } from "react-intl";

import { Page, Section } from "../components";
import { portalLabels as l } from "../labels";
import { portalMessages as m } from "../messages";
import { ProfileForm } from "../ProfileForm";
import { formatDateTime, weekdayShort } from "../utils";

type Rows = Array<[string, string | number | null | undefined]>;

const ReadOnly = ({ rows }: { rows: Rows }) => (
  <Box display="grid" gap={2} __gridTemplateColumns="minmax(140px, 220px) 1fr">
    {rows.map(([label, value]) => (
      <Fragment key={label}>
        <Text size={2} color="default2">
          {label}
        </Text>
        <Text size={3}>{value || "—"}</Text>
      </Fragment>
    ))}
  </Box>
);

export const Profile = () => {
  const intl = useIntl();
  const { data, refetch } = usePortalProfileQuery({ fetchPolicy: "cache-and-network" });
  const [editing, setEditing] = useState(false);
  const profile = data?.myEmployeeProfile;

  if (!profile) {
    return null;
  }

  const personal: Rows = [
    [intl.formatMessage(l.phone), profile.phone],
    [
      intl.formatMessage(l.address),
      [profile.streetAddress, profile.postalCode, profile.city].filter(Boolean).join(", "),
    ],
    [
      intl.formatMessage(l.emergencyContactName),
      [profile.emergencyContactName, profile.emergencyContactPhone].filter(Boolean).join(" · "),
    ],
    [intl.formatMessage(l.nif), profile.nif],
    [intl.formatMessage(l.niss), profile.niss],
    [intl.formatMessage(l.birthDate), profile.birthDate],
    [intl.formatMessage(l.personalEmail), profile.personalEmail],
  ];
  const employment: Rows = [
    [intl.formatMessage(l.fullName), profile.fullName],
    [intl.formatMessage(l.username), profile.username],
    [intl.formatMessage(m.function), profile.roleNames.join(", ")],
    [intl.formatMessage(l.jobTitle), profile.jobTitle],
    [intl.formatMessage(l.hireDate), profile.hireDate],
    [intl.formatMessage(l.manager), profile.managerName],
    [intl.formatMessage(l.workSite), profile.workSite?.name],
    [
      intl.formatMessage(l.normalPeriod),
      intl.formatMessage(l.normalPeriodValue, {
        weekly: profile.weeklyHours,
        daily: profile.dailyHours,
      }),
    ],
    [intl.formatMessage(l.annualVacationDays), profile.annualVacationDays],
    [
      intl.formatMessage(l.schedule),
      profile.weeklySchedule
        .map(row => `${weekdayShort(intl.locale, row.weekday)} ${row.start}–${row.end}`)
        .join(", ") || "—",
    ],
  ];

  return (
    <Page>
      <Section
        title={intl.formatMessage(m.myData)}
        actions={
          !editing && (
            <Button variant="secondary" size="small" onClick={() => setEditing(true)}>
              {intl.formatMessage(m.edit)}
            </Button>
          )
        }
      >
        {editing ? (
          <ProfileForm
            profile={profile}
            missingFields={profile.missingFields}
            submitLabel={m.save}
            onCancel={() => setEditing(false)}
            onSaved={() => {
              setEditing(false);
              refetch();
            }}
          />
        ) : (
          <ReadOnly rows={personal} />
        )}
      </Section>
      <Section title={intl.formatMessage(m.companyData)}>
        <ReadOnly rows={employment} />
        <Text size={2} color="default2">
          {intl.formatMessage(m.dataHint)}
        </Text>
      </Section>
      <Section title={intl.formatMessage(m.documents)}>
        {(data?.currentPolicies ?? []).map(doc => (
          <Box key={doc.id} as="details">
            <Box as="summary" cursor="pointer">
              <Text size={3}>
                {doc.title} (v{doc.version}, {formatDateTime(doc.publishedAt)})
              </Text>
            </Box>
            <Text size={2} __whiteSpace="pre-wrap">
              {doc.body}
            </Text>
          </Box>
        ))}
      </Section>
    </Page>
  );
};
